import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Sparkles, Info, Save, RotateCcw, Loader2, Plus, X,
  User, MessageSquare, Languages, Target, Heart, Shield, Brain,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Slider } from '../../components/ui/slider';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  getOutreachIdentity, updateOutreachIdentity, resetOutreachIdentity,
} from '../../lib/outreach-api';

// ─── Helpers ────────────────────────────────────────────────
const SLIDER_FIELDS = [
  { key: 'professionalism_level', label: 'Professionalism' },
  { key: 'warmth_level',           label: 'Warmth' },
  { key: 'humanization_level',     label: 'Humanization' },
  { key: 'sales_aggressiveness',   label: 'Sales aggressiveness' },
  { key: 'trust_building_priority', label: 'Trust-building priority' },
];

const Section = ({ title, icon: Icon, children, testId }) => (
  <Card data-testid={testId}>
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <Icon className="w-4 h-4 text-amber-400" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">{children}</CardContent>
  </Card>
);

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10px] text-muted-foreground/70">{hint}</p>}
  </div>
);

const SliderField = ({ label, value, onChange, testId }) => (
  <Field label={`${label} — ${value}/100`}>
    <Slider
      value={[value]}
      min={0} max={100} step={5}
      onValueChange={(v) => onChange(v[0])}
      data-testid={testId}
    />
  </Field>
);

const TagList = ({ values, onChange, placeholder, testId }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim();
    if (!t || values.includes(t)) return;
    onChange([...values, t]);
    setDraft('');
  };
  const remove = (t) => onChange(values.filter((v) => v !== t));
  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[28px]">
        {values.map((t) => (
          <Badge
            key={t}
            variant="outline"
            className="gap-1 pl-2 pr-1 py-0.5 text-xs border-amber-500/30"
          >
            {t}
            <button
              type="button"
              onClick={() => remove(t)}
              className="hover:text-red-400 transition"
              data-testid={`tag-remove-${t}`}
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
        {values.length === 0 && (
          <span className="text-xs text-muted-foreground italic">none</span>
        )}
      </div>
      <div className="flex gap-1.5">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder={placeholder}
          className="text-xs"
        />
        <Button type="button" size="sm" variant="outline" onClick={add}>
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

// ─── Page ────────────────────────────────────────────────────

export const OutreachAISettingsPage = () => {
  const [cfg, setCfg] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getOutreachIdentity();
      setCfg(r.data);
      setOriginal(JSON.stringify(r.data));
    } catch (e) {
      toast.error('Failed to load Outreach identity');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const dirty = cfg && original && JSON.stringify(cfg) !== original;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await updateOutreachIdentity(cfg);
      setCfg(r.data);
      setOriginal(JSON.stringify(r.data));
      toast.success('Outreach AI identity saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset Outreach AI identity to defaults?')) return;
    try {
      const r = await resetOutreachIdentity();
      setCfg(r.data);
      setOriginal(JSON.stringify(r.data));
      toast.success('Reset to defaults');
    } catch (e) {
      toast.error('Reset failed');
    }
  };

  if (loading || !cfg) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-4xl" data-testid="outreach-ai-settings">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <span className="text-xs uppercase tracking-widest text-amber-400 font-semibold">
              Outreach AI · Settings
            </span>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Outreach AI Identity</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Configure the Outreach Agent's identity, tone, and conversational behavior.
            These controls only affect the Outreach Agent. The Support Agent is fully isolated.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={reset} data-testid="reset-btn">
            <RotateCcw className="w-3.5 h-3.5 mr-2" />Reset
          </Button>
          <Button
            size="sm"
            disabled={!dirty || saving}
            onClick={save}
            className="bg-amber-500 hover:bg-amber-400 text-black"
            data-testid="save-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-2" />}
            Save changes
          </Button>
        </div>
      </header>

      {/* Isolation banner */}
      <Card className="border-amber-500/30 bg-amber-500/[0.04]" data-testid="isolation-banner">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium">Fully isolated agent</p>
            <p className="text-xs text-muted-foreground">
              The Outreach Agent runs its own prompt, its own LLM session namespace
              (<code className="text-amber-300">outreach_agent_*</code>), and never inherits
              knowledge base content, troubleshooting flows, or Support Agent tone.
              Updates here take effect immediately on the next inbound or outbound message.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Identity */}
      <Section title="Agent Identity" icon={User} testId="section-identity">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Agent name">
            <Input
              value={cfg.agent_name}
              onChange={(e) => set('agent_name', e.target.value)}
              data-testid="input-agent-name"
            />
          </Field>
          <Field label="Agent role">
            <Input
              value={cfg.agent_role}
              onChange={(e) => set('agent_role', e.target.value)}
              data-testid="input-agent-role"
            />
          </Field>
          <Field label="Company / brand">
            <Input
              value={cfg.company_brand}
              onChange={(e) => set('company_brand', e.target.value)}
              data-testid="input-company-brand"
            />
          </Field>
          <Field label="Founder reference">
            <Input
              value={cfg.founder_reference}
              onChange={(e) => set('founder_reference', e.target.value)}
              data-testid="input-founder-reference"
            />
          </Field>
          <Field label="Persona type" hint="How the AI positions itself in the conversation">
            <Select value={cfg.persona_type} onValueChange={(v) => set('persona_type', v)}>
              <SelectTrigger data-testid="select-persona-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="founder">Founder-led</SelectItem>
                <SelectItem value="team">Team member</SelectItem>
                <SelectItem value="growth">Growth team</SelectItem>
                <SelectItem value="outreach">Outreach team</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>
      </Section>

      {/* Tone & Language */}
      <Section title="Tone & Language" icon={Languages} testId="section-tone">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Default language">
            <Select value={cfg.default_language} onValueChange={(v) => set('default_language', v)}>
              <SelectTrigger data-testid="select-default-language"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="english">English (default)</SelectItem>
                <SelectItem value="hinglish">Hinglish</SelectItem>
                <SelectItem value="auto">Mirror lead</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Emoji policy">
            <Select value={cfg.emoji_policy} onValueChange={(v) => set('emoji_policy', v)}>
              <SelectTrigger data-testid="select-emoji-policy"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="never">Never use emojis</SelectItem>
                <SelectItem value="deescalation_only">De-escalation only</SelectItem>
                <SelectItem value="natural">Natural / occasional</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SLIDER_FIELDS.slice(0, 3).map((f) => (
            <SliderField
              key={f.key}
              label={f.label}
              value={cfg[f.key]}
              onChange={(v) => set(f.key, v)}
              testId={`slider-${f.key}`}
            />
          ))}
          <Field label="Allow humor / dry wit">
            <div className="flex items-center gap-3 pt-1">
              <Switch
                checked={cfg.humor_allowed}
                onCheckedChange={(v) => set('humor_allowed', v)}
                data-testid="switch-humor-allowed"
              />
              <span className="text-xs text-muted-foreground">
                {cfg.humor_allowed ? 'Light humor allowed in moderation' : 'No humor / jokes'}
              </span>
            </div>
          </Field>
        </div>
      </Section>

      {/* Conversation Style */}
      <Section title="Conversation Style" icon={MessageSquare} testId="section-style">
        <p className="text-xs text-muted-foreground">
          Conversational ratio per reply. Total should equal 100.
        </p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { key: 'ratio_reaction', label: 'Reaction %' },
            { key: 'ratio_observation', label: 'Observation %' },
            { key: 'ratio_question', label: 'Question %' },
          ].map((f) => (
            <Field key={f.key} label={f.label}>
              <Input
                type="number"
                min={0} max={100}
                value={cfg[f.key]}
                onChange={(e) => set(f.key, Number(e.target.value))}
                data-testid={`input-${f.key}`}
              />
            </Field>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Max questions per message">
            <Input
              type="number" min={0} max={3}
              value={cfg.max_questions_per_message}
              onChange={(e) => set('max_questions_per_message', Number(e.target.value))}
              data-testid="input-max-questions"
            />
          </Field>
          <Field label="Max sentences per message">
            <Input
              type="number" min={1} max={4}
              value={cfg.max_sentences}
              onChange={(e) => set('max_sentences', Number(e.target.value))}
              data-testid="input-max-sentences"
            />
          </Field>
        </div>
      </Section>

      {/* Qualification & Sales */}
      <Section title="Qualification & Sales Strategy" icon={Target} testId="section-qualification">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Qualification speed" hint="How quickly the AI tries to qualify the lead">
            <Select value={cfg.qualification_speed} onValueChange={(v) => set('qualification_speed', v)}>
              <SelectTrigger data-testid="select-qualification-speed"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="slow">Slow — relationship first</SelectItem>
                <SelectItem value="balanced">Balanced</SelectItem>
                <SelectItem value="fast">Fast — qualify early</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <SliderField
            label="Sales aggressiveness"
            value={cfg.sales_aggressiveness}
            onChange={(v) => set('sales_aggressiveness', v)}
            testId="slider-sales-aggressiveness"
          />
        </div>
      </Section>

      {/* Relationship Strategy */}
      <Section title="Relationship & Follow-up Strategy" icon={Heart} testId="section-relationship">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Relationship style">
            <Select value={cfg.relationship_style} onValueChange={(v) => set('relationship_style', v)}>
              <SelectTrigger data-testid="select-relationship-style"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="networking">Networking</SelectItem>
                <SelectItem value="consultative">Consultative</SelectItem>
                <SelectItem value="transactional">Transactional</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Follow-up style">
            <Select value={cfg.follow_up_style} onValueChange={(v) => set('follow_up_style', v)}>
              <SelectTrigger data-testid="select-follow-up-style"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="polite">Polite</SelectItem>
                <SelectItem value="casual">Casual</SelectItem>
                <SelectItem value="minimal">Minimal</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <SliderField
            label="Trust-building priority"
            value={cfg.trust_building_priority}
            onChange={(v) => set('trust_building_priority', v)}
            testId="slider-trust-building"
          />
        </div>
      </Section>

      {/* Sales Boundaries */}
      <Section title="Sales Boundaries" icon={Shield} testId="section-boundaries">
        <Field
          label="Forbidden topics"
          hint="The AI will not mention these unless the lead does first"
        >
          <TagList
            values={cfg.forbidden_topics}
            onChange={(v) => set('forbidden_topics', v)}
            placeholder="add a topic and press Enter…"
            testId="taglist-forbidden-topics"
          />
        </Field>
        <Field
          label="Forbidden phrases"
          hint="Exact phrasings the AI must never use"
        >
          <TagList
            values={cfg.forbidden_phrases}
            onChange={(v) => set('forbidden_phrases', v)}
            placeholder="add a phrase and press Enter…"
            testId="taglist-forbidden-phrases"
          />
        </Field>
      </Section>

      {/* Memory */}
      <Section title="Outreach Memory" icon={Brain} testId="section-memory">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Use long-term memory">
            <div className="flex items-center gap-3 pt-1">
              <Switch
                checked={cfg.use_long_term_memory}
                onCheckedChange={(v) => set('use_long_term_memory', v)}
                data-testid="switch-long-term-memory"
              />
              <span className="text-xs text-muted-foreground">
                {cfg.use_long_term_memory ? 'Use contact summaries' : 'Recent messages only'}
              </span>
            </div>
          </Field>
          <Field label="Use CRM context buckets">
            <div className="flex items-center gap-3 pt-1">
              <Switch
                checked={cfg.use_crm_context_buckets}
                onCheckedChange={(v) => set('use_crm_context_buckets', v)}
                data-testid="switch-crm-context"
              />
              <span className="text-xs text-muted-foreground">
                {cfg.use_crm_context_buckets ? 'Inject CRM source / tags / notes' : 'Ignore CRM context'}
              </span>
            </div>
          </Field>
          <Field label="Memory window (messages)">
            <Input
              type="number" min={2} max={50}
              value={cfg.memory_window_messages}
              onChange={(e) => set('memory_window_messages', Number(e.target.value))}
              data-testid="input-memory-window"
            />
          </Field>
        </div>
      </Section>

      {/* Audit */}
      {cfg.updated_at && (
        <p className="text-[10px] text-muted-foreground" data-testid="audit-line">
          Last updated {new Date(cfg.updated_at).toLocaleString()}
          {cfg.updated_by ? ` by ${cfg.updated_by}` : ''}.
        </p>
      )}
    </div>
  );
};

export default OutreachAISettingsPage;
